import { useEffect, useState } from 'react';

import { Combobox, HookFormResolver, Text, TextArea } from '@dec/shared';
import { Col, Row } from '@dec/shared';
import { Button } from '@dec/shared';
import { ButtonAction } from '@dec/shared';
import { type HookFormMethods } from '@dec/shared';
import { BtnToolbar } from '@dec/shared';
import { label as lblCommon } from 'app/modules/translation';
import { ButtonGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { type CatalogModel } from '../../../Parameters/Catalog/Catalog/models/types';
import { catalogs } from '../../../Parameters/Catalog/Catalog/services/catalog';
import {
  useCatalogList,
  useCatalogListByParent
} from '../../../Parameters/Catalog/Catalog/services/useCatalog';
import { useTaxpayerRequestStore } from '../../stores/useTaxpayerRequestStore';
import { GivenNamesAndSurNames } from '../components/given-names-and-sur-names';
import ProgressBar from '../components/progress-bar';
import { ValidationsResolver } from '../rules';
import { rulesGeneralData } from '../rules/general';
import { label } from '../translation';
import { type GeneralTaxpayer } from '../types';
import VisibleComponent from '../visible-component';

import { type ProgressStepProps } from './types';
const juridic = '2';
const natural = '1';
interface Props {
  onSubmit: (data: any) => void;
  countries: Array<CatalogModel>;
  personTypes: Array<CatalogModel>;
  currentData: GeneralTaxpayer;
  readonly?: boolean;
}
export const GeneralData = ({ onSubmit, countries, personTypes, currentData, readonly }: Props) => {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<GeneralTaxpayer>();
  const [parent, setParent] = useState(currentData?.politicalDivision?.id ?? '');
  const [{ data: politicalDivisions, loading: pdLoading, error: pdError }, execute] =
    useCatalogListByParent(catalogs.politicalDivisions, parent, false);

  useEffect(() => {
    if (currentData?.politicalDivision?.id) {
      execute();
    }
    setState(currentData);
  }, []);

  useEffect(() => {
    if (state !== undefined) {
      execute();
    }
  }, [parent]);

  if (state === undefined) {
    return <></>;
  }

  return (
    <ValidationsResolver rules={rulesGeneralData}>
      {({ validations }: any) => {
        return (
          <HookFormResolver
            nameForm={'formGeneralData'}
            onSubmit={onSubmit}
            validations={validations}
            initialValues={state}
          >
            {({ formState: { errors }, reset, setValue, control, watch }: HookFormMethods) => {
              const personType = watch('personType');

              return (
                <>
                  {' '}
                  <Row>
                    <Col>
                      <Combobox
                        label={lblCommon('personType')}
                        name="personType"
                        data={personTypes}
                        //defaultValue={state.personType}
                        code="id"
                        display="name"
                        readOnly={readonly}
                      />
                    </Col>
                  </Row>
                  <GivenNamesAndSurNames
                    propetyName="taxpayerName"
                    enable={personType?.code == 'individual'}
                    readonly={readonly}
                  />
                  <Row>
                    <Col>
                      <Text
                        name="taxpayerName"
                        label={
                          personType?.code === 'business'
                            ? label(t, 'businessName')
                            : label(t, 'fullName')
                        }
                        readonly={readonly}
                      />
                    </Col>
                  </Row>
                  {personType?.code === 'business' && (
                    <Row>
                      <VisibleComponent fieldCode="tradeName">
                        <Col>
                          <Text
                            readonly={readonly}
                            name="tradeName"
                            label={lblCommon('tradeName')}
                          />
                        </Col>
                      </VisibleComponent>
                    </Row>
                  )}
                  <VisibleComponent fieldCode="address">
                    <Row>
                      <Col>
                        <TextArea readOnly={readonly} label={lblCommon('address')} name="address" />
                      </Col>
                    </Row>
                  </VisibleComponent>
                  <Row>
                    <Col>
                      <Combobox
                        code="id"
                        display="name"
                        data={countries}
                        label={lblCommon('country')}
                        name="country"
                        readOnly={readonly}
                        child="politicalDivision"
                      />
                    </Col>
                    <VisibleComponent fieldCode="politicalDivision">
                      <Col>
                        <Combobox
                          code="id"
                          display="name"
                          data={politicalDivisions}
                          label={lblCommon('politicalDivision')}
                          name="politicalDivision"
                          readOnly={readonly}
                        />
                      </Col>
                    </VisibleComponent>
                  </Row>
                  <Row>
                    <VisibleComponent fieldCode="city">
                      <Col>
                        <Text readonly={readonly} name="city" label={lblCommon('city')} />
                      </Col>
                    </VisibleComponent>

                    <VisibleComponent fieldCode="zipCode">
                      <Col>
                        <Text readonly={readonly} name="zipCode" label={lblCommon('zipCode')} />
                      </Col>
                    </VisibleComponent>
                  </Row>
                  <VisibleComponent fieldCode="website">
                    <Row>
                      <Col>
                        <Text readonly={readonly} name="website" label={lblCommon('website')} />
                      </Col>
                    </Row>
                  </VisibleComponent>
                </>
              );
            }}
          </HookFormResolver>
        );
      }}
    </ValidationsResolver>
  );
};

// eslint-disable-next-line react/display-name
export default (props: ProgressStepProps) => {
  const currentData = useTaxpayerRequestStore((s) => s.form.general);
  const addGeneralData = useTaxpayerRequestStore((s) => s.addGeneralData);
  const [{ data: countries, loading: loadingCountry, error: errorCountry }] = useCatalogList(
    catalogs.countries,
    {},
    true
  );
  const [{ data: personTypes, loading: loadingPersonTypes, error: errorPersonTypes }] =
    useCatalogList(catalogs.personTypes, {}, true);

  const navigate = useNavigate();
  const onSubmit = (data: any) => {
    addGeneralData(data);
    props.nextStep();
  };

  if (loadingCountry || errorCountry || loadingPersonTypes || errorPersonTypes) {
    return <></>;
  }
  const cancel = () => {
    navigate('/');
  };
  return (
    <>
      {' '}
      <GeneralData
        currentData={currentData as GeneralTaxpayer}
        personTypes={personTypes ?? []}
        countries={countries ?? []}
        onSubmit={onSubmit}
      />
      <ProgressBar percentage={props.percentage} />
      <BtnToolbar>
        <ButtonGroup>
          <Button action={ButtonAction.cancel} onClick={() => cancel()} />
        </ButtonGroup>
        <ButtonGroup>
          <Button
            form="formGeneralData"
            variant={'primary'}
            action={ButtonAction.next}
            type={'submit'}
          />
        </ButtonGroup>
      </BtnToolbar>
    </>
  );
};
